import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch } from "./RestService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IScheduled_Report from "../models/IScheduled_Report";
import IProcessed_Report from "../models/IProcessed_Report";
import ICreateScheduledReportDTO from "../models/DTO/ICreateScheduledReportDTO";
import IUpdateScheduledReportDTO from "../models/DTO/IUpdateScheduledReportDTO";

interface IScheduledReportsListResponse {
    success: boolean;
    schedules: IScheduled_Report[];
    message?: string;
}

interface IScheduledReportResponse {
    success: boolean;
    schedule: IScheduled_Report;
    message?: string;
}

interface IRunNowResponse {
    success: boolean;
    report: IProcessed_Report;
    message?: string;
}

interface ISuccessResponse {
    success: boolean;
    message?: string;
}

const handleError = async (response: Response): Promise<void> => {
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText);
    }
};

const getScheduledReports = async (userModel: IUserUI, supplierModel: ISupplierModel): Promise<IScheduledReportsListResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.ScheduledReportsList });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "GET", headers });
    await handleError(response);

    return (await response.json()) as IScheduledReportsListResponse;
};

const getScheduledReport = async (guid: string, userModel: IUserUI, supplierModel: ISupplierModel): Promise<IScheduledReportResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.ScheduledReportsGet, params: { guid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "GET", headers });
    await handleError(response);

    return (await response.json()) as IScheduledReportResponse;
};

const createScheduledReport = async (
    dto: ICreateScheduledReportDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IScheduledReportResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.ScheduledReportsCreate });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(dto),
    });
    await handleError(response);

    return (await response.json()) as IScheduledReportResponse;
};

const updateScheduledReport = async (
    guid: string,
    dto: IUpdateScheduledReportDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IScheduledReportResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.ScheduledReportsUpdate, params: { guid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(dto),
    });
    await handleError(response);

    return (await response.json()) as IScheduledReportResponse;
};

const deleteScheduledReport = async (guid: string, userModel: IUserUI, supplierModel: ISupplierModel): Promise<ISuccessResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.ScheduledReportsDelete, params: { guid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "DELETE", headers });
    await handleError(response);

    // A successful DELETE may return 204 No Content (empty body); only parse JSON when there is a body.
    if (response.status === 204 || response.headers.get("content-length") === "0") {
        return { success: true };
    }

    return (await response.json()) as ISuccessResponse;
};

const toggleScheduledReport = async (
    guid: string,
    active: boolean,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IScheduledReportResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.ScheduledReportsToggle, params: { guid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ active }),
    });
    await handleError(response);

    return (await response.json()) as IScheduledReportResponse;
};

const runNowScheduledReport = async (guid: string, userModel: IUserUI, supplierModel: ISupplierModel): Promise<IRunNowResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.ScheduledReportsRunNow, params: { guid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "POST", headers });
    await handleError(response);

    return (await response.json()) as IRunNowResponse;
};

const ScheduledReportsService = {
    getScheduledReports,
    getScheduledReport,
    createScheduledReport,
    updateScheduledReport,
    deleteScheduledReport,
    toggleScheduledReport,
    runNowScheduledReport,
};

export default ScheduledReportsService;
